<template>
  <div
    class="badge-square"
    :class="{
      'badge-square--primary': primary,
      'badge-square--secondary': secondary,
      'badge-square--custom-color': customColorProp,
      'badge-square--grey': grey,
      'badge-square__small': size === 'small',
      'badge-square__medium': size === 'medium',
      'badge-square__large': size === 'large',
      'badge-square__extra-small': size === 'extra-small'
    }"
    :style="containerStyle"
    :tabindex="tabIndex"
  >
    <div v-if="hasLeftIcon">
      <SvgDisplay src="Icon-X-Small" :class="badgeClassesIcon"></SvgDisplay>
    </div>
    <TextBase
      :class="badgeClassesText"
      :text="badgeText"
      type="label"
      color="inherit"
      text-wrap="wrap"
      overflow-tooltip
    />
    <div v-if="hasRightIcon" :class="badgeClassesIcon">
      <SvgDisplay src="Icon-X-Small" :class="badgeClassesIcon"></SvgDisplay>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { toRefs, computed, CSSProperties } from 'vue'
import SvgDisplay from './SvgDisplay.vue'
import TextBase from './TextBase.vue'
const props = withDefaults(
  defineProps<{
    badgeText: string
    customColor?: string | undefined
    grey?: boolean
    hasLeftIcon?: boolean
    hasRightIcon?: boolean
    primary?: boolean
    secondary?: boolean
    size?: 'extra-small' | 'small' | 'medium' | 'large'
    tabIndex?: number
    containerStyle?: CSSProperties | undefined
  }>(),
  {
    customColor: undefined,
    grey: false,
    hasLeftIcon: false,
    hasRightIcon: false,
    primary: true,
    secondary: false,
    size: 'medium',
    tabIndex: -1,
    containerStyle: undefined
  }
)
const { badgeText, size } = toRefs(props)

const badgeClassesText = computed(() => {
  if (size.value === 'medium') return 'badge-square__text'
  if (size.value === 'small') return 'badge-square__text__small'
  if (size.value === 'large') return 'badge-square__text__large'
  if (size.value === 'extra-small') return 'badge-square__text__extra-small'
  else return ''
})
const badgeClassesIcon = computed(() => {
  if (size.value === 'extra-small') {
    return 'badge-square__icon__extra-small'
  } else return 'badge-square__icon'
})
const customColorProp = computed(() => props.customColor)
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.badge-square {
  align-items: center;
  border-radius: 4px;
  display: flex;
  gap: 4px;
  height: fit-content;
  text-align: center;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  &__small {
    padding: 4px 8px;
  }
  &__extra-small {
    padding: 4px 8px;
  }
  &__medium {
    padding: 8px 12px;
  }
  &__large {
    padding: 8px 16px;
  }
  &--primary {
    background-color: variables.$color-primary-light;
  }
  &--secondary {
    background-color: variables.$color-secondary-light;
  }
  &--grey {
    background-color: variables.$color-greyscale-light;
  }
  &--custom-color {
    background-color: v-bind(customColorProp);
  }
  &__icon {
    height: 16px;
    width: 16px;
    &__extra-small {
      align-items: center;
      display: flex;
      height: 8px;
      width: 8px;
    }
  }
  &__text {
    align-items: center;
    display: flex;
    @include font-mixins.desktop-regular-label;
    color: variables.$color-greyscale-content;
    &__extra-small {
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-regular-tiny;
      align-items: center;
      display: flex;
    }
    &__small {
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-regular-label;
    }
    &__large {
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-bold-paragraph;
    }
  }
}
// currenlty we don't need hover (or focus), when we want to use it, we can use this
// :hover.badge-square {
//   background-color: variables.$color-primary;
//   .badge-square__text {
//     color: variables.$color-pure-white;
//     &__extra-small {
//       color: variables.$color-pure-white;
//     }
//     &__small {
//       color: variables.$color-pure-white;
//     }
//     &__large {
//       color: variables.$color-pure-white;
//     }
//   }
//   .badge-square__icon {
//     color: variables.$color-pure-white;
//     &__extra-small {
//       color: variables.$color-pure-white;
//     }
//   }
// }
// :focus.badge-square {
//   &--primary {
//     border-radius: 4px;
//     border: 2px solid variables.$color-primary;
//   }
//   &--grey {
//     border-radius: 4px;
//     border: 2px solid variables.$color-greyscale-secondary;
//   }
//   &--secondary {
//     border-radius: 4px;
//     border: 2px solid variables.$color-secondary;
//   }
// }
</style>
